/**
 * `<Tooltip>` — Radix-backed tooltip primitive.
 *
 * Foundations spec: design/design-system-foundations.md §9.7.
 * Wraps `@radix-ui/react-tooltip` with the locked surface composition
 * (deepest-layer chrome bg, primary-text fg, strong-border ring, md radius
 * + shadow). Fade + 4px translate animation honors reduced-motion via the
 * global @media block in styles/index.css.
 *
 * Usage:
 *   <Tooltip content="Copy SHA">
 *     <button>{shortSha}</button>
 *   </Tooltip>
 *
 * Multiple tooltips on a page share the single `<TooltipProvider>` mounted
 * at the app root (a future shell-level wiring; until then each Tooltip
 * mounts its own — Radix handles deduping fine).
 *
 * 30f Phase B1 — additive only. No existing pages consume yet.
 */
import * as TooltipPrimitive from "@radix-ui/react-tooltip";
import type { ReactNode } from "react";
import { usePortalHost } from "@/components/v2/layout/PortalHostContext";

interface TooltipProps {
  /** Tooltip body — string or arbitrary node. */
  content: ReactNode;
  /** Anchor side. Defaults to "top". */
  side?: "top" | "right" | "bottom" | "left";
  /** Open delay in ms. Defaults to 500 per spec. */
  delayMs?: number;
  /** Trigger element (a single child). */
  children: ReactNode;
}

export function Tooltip({
  content,
  side = "top",
  delayMs = 500,
  children,
}: TooltipProps): JSX.Element {
  // Mount inside the zoomed shell host so the tooltip matches app scale under
  // the app-scale dial; `?? undefined` falls back to document.body off the shell.
  const container = usePortalHost() ?? undefined;
  return (
    <TooltipPrimitive.Provider delayDuration={delayMs}>
      <TooltipPrimitive.Root>
        <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
        <TooltipPrimitive.Portal container={container}>
          <TooltipPrimitive.Content
            side={side}
            sideOffset={6}
            // [zoom:var(--app-scale)] scales the tooltip to match the app-scale
            // dial; the body-zoom-1 host keeps positioning correct. See
            // PortalHostContext.
            className="[zoom:var(--app-scale)] z-50 rounded-md border border-border-strong bg-surface-app-chrome px-2.5 py-1.5 text-xs text-text-primary shadow-md"
          >
            {content}
            <TooltipPrimitive.Arrow className="fill-surface-app-chrome" />
          </TooltipPrimitive.Content>
        </TooltipPrimitive.Portal>
      </TooltipPrimitive.Root>
    </TooltipPrimitive.Provider>
  );
}
